// Video Generation Page Logic
import { loadAuthenticatedMedia } from './utils/authenticated-media-loader.js';
import {
  getStoredTransformSource,
  clearStoredTransformSource,
} from './utils/transform-source.js';

// Model cost estimates (per 5 seconds)
// Canonical source: VIDEO_MODEL_COSTS in @mcp-image-server/shared/types.ts
const MODEL_COSTS: Record<string, { '5': number; '10': number }> = {
  'kwaivgi/kling-v2.6': { '5': 0.5, '10': 1.0 },
  'kwaivgi/kling-v2.5-turbo-pro': { '5': 0.3, '10': 0.6 },
  'kwaivgi/kling-v2.1': { '5': 0.25, '10': 0.5 },
  'kwaivgi/kling-v1.6-pro': { '5': 0.2, '10': 0.4 },
};

// Global state
let inputImage: File | null = null;
let currentVideoUrl: string | null = null;

export function initVideoGenerationPage() {
  // Check for videoUrl parameter (viewing existing video)
  const urlParams = new URLSearchParams(window.location.search);
  const videoUrl = urlParams.get('videoUrl');

  if (videoUrl) {
    // Direct viewer mode - hide upload/config UI, show viewer
    const uploadCard = document.getElementById('uploadCard');
    const configCard = document.getElementById('configCard');
    const resultsCard = document.getElementById('resultsCard');

    if (uploadCard) uploadCard.style.display = 'none';
    if (configCard) configCard.style.display = 'none';
    if (resultsCard) {
      resultsCard.style.display = 'block';
      void loadVideo(videoUrl);

      // Hide metadata section since we don't have that info
      const metadataSection = document.getElementById('resultMetadata');
      if (metadataSection) metadataSection.style.display = 'none';

      // Set download link
      const downloadLink = document.getElementById(
        'downloadVideo'
      ) as HTMLAnchorElement;
      if (downloadLink) {
        downloadLink.href = videoUrl;
        downloadLink.download = 'video.mp4';
      }
    }

    return; // Skip normal initialization
  }

  // Normal initialization for upload/generation mode
  // Check for sessionStorage data (from detail page re-transform)
  const stored = getStoredTransformSource();

  // Get DOM elements
  const modelSelect = document.getElementById('model') as HTMLSelectElement;
  const durationSelect = document.getElementById(
    'duration'
  ) as HTMLSelectElement;
  const imageUploadArea = document.getElementById('imageUploadArea');
  const imageInput = document.getElementById('imageInput') as HTMLInputElement;
  const removeImageBtn = document.getElementById('removeImage');
  const generateForm = document.getElementById('generateForm');
  const newGenerationBtn = document.getElementById('newGenerationBtn');

  // Load image from sessionStorage if present
  if (stored) {
    void loadImageFromUrl(stored.source!, stored.sourceType);

    // Clear sessionStorage after loading
    clearStoredTransformSource();
  }

  // Model change handler - show/hide audio option
  modelSelect?.addEventListener('change', () => {
    updateAudioVisibility();
    updateCostEstimate();
  });

  // Duration change handler
  durationSelect?.addEventListener('change', () => {
    updateCostEstimate();
  });

  // Initialize audio visibility and cost
  updateAudioVisibility();
  updateCostEstimate();

  // Upload area click handler
  imageUploadArea?.addEventListener('click', () => imageInput?.click());

  // Drag and drop support
  imageUploadArea?.addEventListener('dragover', (e) => {
    e.preventDefault();
    imageUploadArea.classList.add('drag-over');
  });

  imageUploadArea?.addEventListener('dragleave', () => {
    imageUploadArea.classList.remove('drag-over');
  });

  imageUploadArea?.addEventListener('drop', (e) => {
    e.preventDefault();
    imageUploadArea.classList.remove('drag-over');
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  });

  // File input change handler
  imageInput?.addEventListener('change', (e) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  });

  // Remove image button
  removeImageBtn?.addEventListener('click', removeImage);

  // Form submit
  generateForm?.addEventListener('submit', handleSubmit);

  // New generation button
  newGenerationBtn?.addEventListener('click', resetPage);
}

function updateAudioVisibility() {
  const model = (document.getElementById('model') as HTMLSelectElement)?.value;
  const audioGroup = document.getElementById('audioGroup');

  if (audioGroup) {
    audioGroup.style.display =
      model === 'kwaivgi/kling-v2.6' ? 'block' : 'none';
  }
}

function updateCostEstimate() {
  const model = (document.getElementById('model') as HTMLSelectElement)?.value;
  const duration = (document.getElementById('duration') as HTMLSelectElement)
    ?.value as '5' | '10';
  const costValue = document.getElementById('costValue');

  if (costValue && model && MODEL_COSTS[model]) {
    const cost = MODEL_COSTS[model][duration];
    costValue.textContent = `$${cost.toFixed(2)}`;
  }
}

function handleFileSelect(file: File) {
  // Validate file type
  if (!file.type.startsWith('image/')) {
    showMessage('Selecteer een afbeelding (PNG, JPG, WebP)', 'error');
    return;
  }

  // Validate file size (50MB max)
  if (file.size > 50 * 1024 * 1024) {
    showMessage('Bestand is te groot (max 50MB)', 'error');
    return;
  }

  inputImage = file;

  // Show preview
  const reader = new FileReader();
  reader.onload = (e) => {
    const previewImg = document.getElementById(
      'previewImg'
    ) as HTMLImageElement;
    const imagePreview = document.getElementById('imagePreview');
    const imageUploadArea = document.getElementById('imageUploadArea');
    const configCard = document.getElementById('configCard');

    if (previewImg && e.target?.result) {
      previewImg.src = e.target.result as string;
    }
    if (imagePreview) imagePreview.style.display = 'block';
    if (imageUploadArea) imageUploadArea.style.display = 'none';
    if (configCard) configCard.style.display = 'block';
  };
  reader.readAsDataURL(file);
}

async function loadImageFromUrl(
  url: string,
  _sourceType: string
): Promise<void> {
  try {
    // Use authenticated fetch to get the image
    const response = await (window as any).authenticatedFetch(url);
    if (!response.ok) {
      throw new Error(`Failed to load image: ${response.statusText}`);
    }

    const blob = await response.blob();
    const filename = url.split('/').pop() || 'image.png';
    const file = new File([blob], filename, { type: blob.type });

    handleFileSelect(file);
  } catch (error) {
    console.error('Failed to load image from URL:', error);
    showMessage('Kon afbeelding niet laden', 'error');
  }
}

function removeImage() {
  inputImage = null;

  const previewImg = document.getElementById('previewImg') as HTMLImageElement;
  const imagePreview = document.getElementById('imagePreview');
  const imageUploadArea = document.getElementById('imageUploadArea');
  const configCard = document.getElementById('configCard');
  const imageInput = document.getElementById('imageInput') as HTMLInputElement;

  if (previewImg) previewImg.src = '';
  if (imagePreview) imagePreview.style.display = 'none';
  if (imageUploadArea) imageUploadArea.style.display = 'block';
  if (configCard) configCard.style.display = 'none';
  if (imageInput) imageInput.value = '';
}

async function handleSubmit(e: Event) {
  e.preventDefault();

  if (!inputImage) {
    showMessage('Upload eerst een afbeelding', 'error');
    return;
  }

  const model = (document.getElementById('model') as HTMLSelectElement)?.value;
  const prompt = (document.getElementById('prompt') as HTMLTextAreaElement)
    ?.value;
  const negativePrompt = (
    document.getElementById('negativePrompt') as HTMLTextAreaElement
  )?.value;
  const duration = (document.getElementById('duration') as HTMLSelectElement)
    ?.value;
  const aspectRatio = (
    document.getElementById('aspectRatio') as HTMLSelectElement
  )?.value;
  const enableAudio = (
    document.getElementById('enableAudio') as HTMLInputElement
  )?.checked;

  if (!prompt) {
    showMessage('Voer een prompt in', 'error');
    return;
  }

  // Show loading state
  const uploadCard = document.getElementById('uploadCard');
  const configCard = document.getElementById('configCard');
  const loadingCard = document.getElementById('loadingCard');
  const generateBtn = document.getElementById(
    'generateBtn'
  ) as HTMLButtonElement;

  if (uploadCard) uploadCard.style.display = 'none';
  if (configCard) configCard.style.display = 'none';
  if (loadingCard) loadingCard.style.display = 'block';
  if (generateBtn) generateBtn.disabled = true;

  // Start progress animation
  const progressBar = document.getElementById('progressBar');
  let progress = 0;
  const progressInterval = setInterval(() => {
    progress = Math.min(progress + 0.5, 95);
    if (progressBar) {
      progressBar.style.width = `${progress}%`;
    }
  }, 1000);

  try {
    // Build form data
    const formData = new FormData();
    formData.append('imageFile', inputImage);
    formData.append('model', model);
    formData.append('prompt', prompt);
    if (negativePrompt) {
      formData.append('negativePrompt', negativePrompt);
    }
    formData.append('duration', duration);
    formData.append('aspectRatio', aspectRatio);
    formData.append('enableAudio', enableAudio ? 'true' : 'false');

    // Send request
    const response = await (window as any).authenticatedFetch(
      '/api/image-to-video',
      {
        method: 'POST',
        body: formData,
      }
    );

    clearInterval(progressInterval);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Video generatie mislukt');
    }

    const result = await response.json();

    // Complete progress bar
    if (progressBar) {
      progressBar.style.width = '100%';
    }

    // Show results
    await showResults(result);
  } catch (error) {
    clearInterval(progressInterval);
    console.error('Video generation failed:', error);
    showMessage(
      error instanceof Error ? error.message : 'Video generatie mislukt',
      'error'
    );

    // Reset UI
    if (uploadCard) uploadCard.style.display = 'block';
    if (configCard) configCard.style.display = 'block';
    if (loadingCard) loadingCard.style.display = 'none';
    if (generateBtn) generateBtn.disabled = false;
  }
}

async function showResults(result: {
  videoId: string;
  videoUrl: string;
  thumbnailUrl?: string;
  metadata: {
    modelName: string;
    duration: number;
    aspectRatio: string;
    enableAudio: boolean;
    cost: number;
    generationTime: number;
    fileSizeBytes: number;
  };
}) {
  const loadingCard = document.getElementById('loadingCard');
  const resultsCard = document.getElementById('resultsCard');

  if (loadingCard) loadingCard.style.display = 'none';
  if (resultsCard) resultsCard.style.display = 'block';

  // Store current video URL
  currentVideoUrl = result.videoUrl;

  // Load video
  await loadVideo(result.videoUrl);

  // Update metadata
  const resultModel = document.getElementById('resultModel');
  const resultDuration = document.getElementById('resultDuration');
  const resultAspectRatio = document.getElementById('resultAspectRatio');
  const resultFileSize = document.getElementById('resultFileSize');
  const resultTime = document.getElementById('resultTime');
  const resultCost = document.getElementById('resultCost');

  const modelDisplayNames: Record<string, string> = {
    'kwaivgi/kling-v2.6': 'Kling v2.6 (Premium)',
    'kwaivgi/kling-v2.5-turbo-pro': 'Kling v2.5 Turbo Pro',
    'kwaivgi/kling-v2.1': 'Kling v2.1',
    'kwaivgi/kling-v1.6-pro': 'Kling v1.6 Pro',
  };

  if (resultModel) {
    resultModel.textContent =
      modelDisplayNames[result.metadata.modelName] || result.metadata.modelName;
  }
  if (resultDuration) {
    resultDuration.textContent = `${result.metadata.duration} seconden`;
  }
  if (resultAspectRatio) {
    resultAspectRatio.textContent = result.metadata.aspectRatio;
  }
  if (resultFileSize) {
    const sizeMB = (result.metadata.fileSizeBytes / 1024 / 1024).toFixed(2);
    resultFileSize.textContent = `${sizeMB} MB`;
  }
  if (resultTime) {
    const timeSeconds = (result.metadata.generationTime / 1000).toFixed(1);
    resultTime.textContent = `${timeSeconds}s`;
  }
  if (resultCost) {
    resultCost.textContent = `$${result.metadata.cost.toFixed(2)}`;
  }

  // Setup download link
  const downloadLink = document.getElementById(
    'downloadVideo'
  ) as HTMLAnchorElement;
  if (downloadLink && currentVideoUrl) {
    // Create authenticated download
    try {
      const blobUrl = await loadAuthenticatedMedia(currentVideoUrl);
      downloadLink.href = blobUrl;
      downloadLink.download = `video-${result.videoId}.mp4`;
    } catch (error) {
      console.error('Failed to setup download link:', error);
      downloadLink.href = currentVideoUrl;
    }
  }

  showMessage('Video succesvol gegenereerd!', 'success');
}

async function loadVideo(videoUrl: string): Promise<void> {
  const videoPlayer = document.getElementById(
    'videoPlayer'
  ) as HTMLVideoElement;
  const videoSource = document.getElementById(
    'videoSource'
  ) as HTMLSourceElement;

  if (!videoPlayer || !videoSource) {
    console.error('Video player elements not found');
    return;
  }

  try {
    // Load video with authentication
    const blobUrl = await loadAuthenticatedMedia(videoUrl);

    videoSource.src = blobUrl;
    videoPlayer.load();

    // Clean up blob URL when video is unloaded
    videoPlayer.onloadeddata = () => {
      console.log('Video loaded successfully');
    };
  } catch (error) {
    console.error('Failed to load video:', error);
    showMessage('Kon video niet laden', 'error');
  }
}

function resetPage() {
  // Reset state
  inputImage = null;
  currentVideoUrl = null;

  // Reset form
  const generateForm = document.getElementById(
    'generateForm'
  ) as HTMLFormElement;
  generateForm?.reset();

  // Reset UI
  const previewImg = document.getElementById('previewImg') as HTMLImageElement;
  const imagePreview = document.getElementById('imagePreview');
  const imageUploadArea = document.getElementById('imageUploadArea');
  const uploadCard = document.getElementById('uploadCard');
  const configCard = document.getElementById('configCard');
  const loadingCard = document.getElementById('loadingCard');
  const resultsCard = document.getElementById('resultsCard');
  const progressBar = document.getElementById('progressBar');
  const generateBtn = document.getElementById(
    'generateBtn'
  ) as HTMLButtonElement;
  const imageInput = document.getElementById('imageInput') as HTMLInputElement;

  if (previewImg) previewImg.src = '';
  if (imagePreview) imagePreview.style.display = 'none';
  if (imageUploadArea) imageUploadArea.style.display = 'block';
  if (uploadCard) uploadCard.style.display = 'block';
  if (configCard) configCard.style.display = 'none';
  if (loadingCard) loadingCard.style.display = 'none';
  if (resultsCard) resultsCard.style.display = 'none';
  if (progressBar) progressBar.style.width = '0%';
  if (generateBtn) generateBtn.disabled = false;
  if (imageInput) imageInput.value = '';

  // Reset cost estimate
  updateAudioVisibility();
  updateCostEstimate();

  // Clear message
  const messageDiv = document.getElementById('message');
  if (messageDiv) messageDiv.innerHTML = '';
}

function showMessage(
  message: string,
  type: 'success' | 'error' | 'info' = 'info'
) {
  const messageDiv = document.getElementById('message');
  if (messageDiv) {
    messageDiv.innerHTML = `<div class="message ${type}">${message}</div>`;

    // Auto-clear after 5 seconds
    setTimeout(() => {
      messageDiv.innerHTML = '';
    }, 5000);
  }
}

// Export init function to window for HTML script to call
(
  window as unknown as { initVideoGenerationPage: () => void }
).initVideoGenerationPage = initVideoGenerationPage;
